import React, { useState, useEffect } from 'react';
import { 
  DollarSign, 
  TrendingUp, 
  TrendingDown, 
  Calendar,
  Plus,
  FileText,
  Printer,
  Search,
  AlertCircle,
  X,
  Download,
  CreditCard,
  Banknote,
  Pencil,
  Trash2,
  Loader2
} from 'lucide-react';
import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';
import { useTransactions } from '../lib/hooks/useTransactions';
import { useTranslation } from 'react-i18next';
import type { Database } from '../lib/database.types';
import { getPaymentMethodLabel, type PaymentMethod } from '../lib/utils';

type Transaction = Database['public']['Tables']['transactions']['Row'];
type IncomeType = Database['public']['Tables']['income_types']['Row'];
type ExpenseType = Database['public']['Tables']['expense_types']['Row'];
type Period = 'today' | 'yesterday' | 'week' | 'month' | 'all';

type AddTransactionFormData = {
  type: 'income' | 'expense';
  amount: string;
  description: string;
  incomeTypeId: string;
  customIncomeTypeName: string;
  expenseTypeId: string;
  customExpenseTypeName: string;
  paymentMethod: PaymentMethod;
};

const DailyCash = () => {
  const { t } = useTranslation();
  const { getTransactions, addTransaction, getSummary, getIncomeTypes, getExpenseTypes, createIncomeType, createExpenseType, updateIncomeType, updateExpenseType, loading, error } = useTransactions();
  const [selectedPeriod, setSelectedPeriod] = useState<Period>('today');
  const [searchQuery, setSearchQuery] = useState('');
  const [showAddModal, setShowAddModal] = useState(false);
  const [transactions, setTransactions] = useState<Transaction[]>([]);
  const [summary, setSummary] = useState({
    cash: { income: 0, expenses: 0, balance: 0 },
    card: { income: 0, expenses: 0, balance: 0 },
    total: { income: 0, expenses: 0, balance: 0 }
  });
  const [incomeTypes, setIncomeTypes] = useState<IncomeType[]>([]);
  const [expenseTypes, setExpenseTypes] = useState<ExpenseType[]>([]);
  const [formData, setFormData] = useState<AddTransactionFormData>({
    type: 'income',
    amount: '',
    description: '',
    incomeTypeId: '',
    customIncomeTypeName: '',
    expenseTypeId: '',
    customExpenseTypeName: '',
    paymentMethod: 'cash'
  });
  const [formError, setFormError] = useState<string | null>(null);
  const [isExporting, setIsExporting] = useState(false);

  useEffect(() => {
    loadData();
    loadIncomeTypes();
    loadExpenseTypes();
  }, [selectedPeriod]);

  const loadData = async () => {
    const [transactionsData, summaryData] = await Promise.all([
      getTransactions(selectedPeriod),
      getSummary(selectedPeriod)
    ]);

    setTransactions(transactionsData || []);
    if (summaryData) {
      setSummary(summaryData);
    }
  };

  // Inactive (deleted) types are loaded too so old transactions keep their
  // category name; only active ones are offered in the form.
  const loadIncomeTypes = async () => {
    const types = await getIncomeTypes(true);
    setIncomeTypes(types);
  };

  const loadExpenseTypes = async () => {
    const types = await getExpenseTypes(true);
    setExpenseTypes(types);
  };

  // --- Category management (rename / delete), like repair device types ---
  const [editingCashType, setEditingCashType] = useState<
    { kind: 'income'; type: IncomeType } | { kind: 'expense'; type: ExpenseType } | null
  >(null);
  const [editCashTypeName, setEditCashTypeName] = useState('');
  const [confirmDeleteCashType, setConfirmDeleteCashType] = useState(false);
  const [savingCashType, setSavingCashType] = useState(false);
  const [cashTypeError, setCashTypeError] = useState<string | null>(null);

  const openEditCashType = (
    e: React.MouseEvent,
    edit: { kind: 'income'; type: IncomeType } | { kind: 'expense'; type: ExpenseType }
  ) => {
    e.stopPropagation();
    setEditingCashType(edit);
    setEditCashTypeName(edit.type.name);
    setConfirmDeleteCashType(false);
    setCashTypeError(null);
  };

  const closeEditCashType = () => {
    setEditingCashType(null);
    setConfirmDeleteCashType(false);
    setCashTypeError(null);
  };

  const applyCashTypeUpdate = async (updates: { name?: string; is_active?: boolean }) => {
    if (!editingCashType) return;
    setSavingCashType(true);
    setCashTypeError(null);
    const { kind, type } = editingCashType;

    if (kind === 'income') {
      const updated = await updateIncomeType(type.id, updates);
      if (!updated) {
        setSavingCashType(false);
        setCashTypeError("Saqlashda xatolik yuz berdi");
        return;
      }
      setIncomeTypes((prev) => prev.map((t) => (t.id === updated.id ? updated : t)));
      // A deleted category can't stay selected in the open form
      if (updates.is_active === false && formData.incomeTypeId === type.id) {
        setFormData((prev) => ({ ...prev, incomeTypeId: '' }));
      }
    } else {
      const updated = await updateExpenseType(type.id, updates);
      if (!updated) {
        setSavingCashType(false);
        setCashTypeError("Saqlashda xatolik yuz berdi");
        return;
      }
      setExpenseTypes((prev) => prev.map((t) => (t.id === updated.id ? updated : t)));
      if (updates.is_active === false && formData.expenseTypeId === type.id) {
        setFormData((prev) => ({ ...prev, expenseTypeId: '' }));
      }
    }

    setSavingCashType(false);
    closeEditCashType();
  };

  const handleSaveCashType = () => {
    const name = editCashTypeName.trim();
    if (!name) return;
    applyCashTypeUpdate({ name });
  };

  const handleDeleteCashType = () => {
    // First click asks for confirmation, second click deletes
    if (!confirmDeleteCashType) {
      setConfirmDeleteCashType(true);
      return;
    }
    applyCashTypeUpdate({ is_active: false });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setFormError(null);

    const amount = parseFloat(formData.amount);
    if (isNaN(amount) || amount <= 0) {
      setFormError(t('invalidAmount'));
      return;
    }

    // Validate type selection based on transaction type
    if (formData.type === 'income' && !formData.incomeTypeId) {
      setFormError(t('incomeTypeRequired'));
      return;
    }

    if (formData.type === 'expense' && !formData.expenseTypeId) {
      setFormError(t('expenseTypeRequired'));
      return;
    }

    // Handle custom income type creation
    let finalIncomeTypeId = formData.incomeTypeId;
    let finalExpenseTypeId = formData.expenseTypeId;
    
    if (formData.type === 'income') {
      if (formData.incomeTypeId === 'boshqa') {
        if (!formData.customIncomeTypeName.trim()) {
          setFormError(t('customIncomeTypeRequired'));
          return;
        }
        // Create new income type
        const newType = await createIncomeType(formData.customIncomeTypeName.trim());
        if (!newType) {
          setFormError('Failed to create custom income type');
          return;
        }
        finalIncomeTypeId = newType.id;
        // Refresh income types
        await loadIncomeTypes();
      }
    } else if (formData.type === 'expense') {
      if (formData.expenseTypeId === 'boshqa') {
        if (!formData.customExpenseTypeName.trim()) {
          setFormError(t('customExpenseTypeRequired'));
          return;
        }
        // Create new expense type
        const newType = await createExpenseType(formData.customExpenseTypeName.trim());
        if (!newType) {
          setFormError('Failed to create custom expense type');
          return;
        }
        finalExpenseTypeId = newType.id;
        // Refresh expense types
        await loadExpenseTypes();
      }
    }

    const transaction = await addTransaction({
      type: formData.type,
      amount,
      description: formData.description || null,
      income_type_id: formData.type === 'income' ? finalIncomeTypeId : null,
      expense_type_id: formData.type === 'expense' ? finalExpenseTypeId : null,
      payment_method: formData.paymentMethod
    });

    if (transaction) {
      setShowAddModal(false);
      setFormData({ 
        type: 'income', 
        amount: '', 
        description: '', 
        incomeTypeId: '', 
        customIncomeTypeName: '',
        expenseTypeId: '',
        customExpenseTypeName: '',
        paymentMethod: 'cash'
      });
      loadData();
    }
  };

  const getIncomeTypeName = (incomeTypeId: string | null) => {
    if (!incomeTypeId) return null;
    const type = incomeTypes.find(t => t.id === incomeTypeId);
    return type?.name || null;
  };

  const getExpenseTypeName = (expenseTypeId: string | null) => {
    if (!expenseTypeId) return null;
    const type = expenseTypes.find(t => t.id === expenseTypeId);
    return type?.name || null;
  };

  const getTransactionDisplayName = (transaction: Transaction) => {
    if (transaction.type === 'income') {
      // For income, try to get the income type name
      const incomeTypeName = getIncomeTypeName(transaction.income_type_id);
      if (incomeTypeName) {
        // Capitalize first letter
        return incomeTypeName.charAt(0).toUpperCase() + incomeTypeName.slice(1);
      }
      return transaction.description || t('income');
    } else {
      // For expense, try to get the expense type name
      const expenseTypeName = getExpenseTypeName(transaction.expense_type_id);
      if (expenseTypeName) {
        // Capitalize first letter
        return expenseTypeName.charAt(0).toUpperCase() + expenseTypeName.slice(1);
      }
      return transaction.description || t('expense');
    }
  };

  const handleExport = async () => {
    try {
      const doc = new jsPDF();
      
      // Add title
      doc.setFontSize(20);
      doc.text('Kunlik kassa hisoboti', 20, 20);
      
      // Add date
      doc.setFontSize(12);
      doc.text(`Sana: ${new Date().toLocaleDateString()}`, 20, 30);
      
      // Add summary
      doc.text('Xulosa:', 20, 45);
      doc.text(`Kirim: ${summary.total.income.toLocaleString()} UZS`, 30, 55);
      doc.text(`Chiqim: ${summary.total.expenses.toLocaleString()} UZS`, 30, 65);
      doc.text(`Balans: ${summary.total.balance.toLocaleString()} UZS`, 30, 75);
      
      // Add transactions table
      const tableHeaders = ['Sana', 'Turi', 'Summa', "To'lov usuli", 'Izoh'];
      const tableData = filteredTransactions.map(t => [
        new Date(t.created_at).toLocaleDateString(),
        getTransactionDisplayName(t),
        `${t.amount.toLocaleString()} UZS`,
        t.payment_method ? getPaymentMethodLabel(t.payment_method) : '-',
        t.description || '-'
      ]);
      
      autoTable(doc, {
        head: [tableHeaders],
        body: tableData,
        startY: 90,
        styles: { fontSize: 8 }
      });
      
      // Save the PDF
      doc.save(`kunlik-kassa-${new Date().toISOString().split('T')[0]}.pdf`);
    } catch (error) {
      console.error('Export error:', error);
      alert('Eksport qilishda xatolik yuz berdi');
    }
  };

  const getDateRange = () => {
    const now = new Date();
    let startDate = new Date();
    let endDate = new Date();

    switch (selectedPeriod) {
      case 'yesterday':
        startDate.setDate(startDate.getDate() - 1);
        endDate = new Date(startDate);
        break;
      case 'week':
        startDate.setDate(startDate.getDate() - 7);
        break;
      case 'month':
        startDate.setMonth(startDate.getMonth() - 1);
        break;
      case 'all':
        return { start: null, end: null };
      default:
        // today
        break;
    }

    startDate.setHours(0, 0, 0, 0);
    endDate.setHours(23, 59, 59, 999);

    return { start: startDate, end: endDate };
  };

  const handleExportXLSX = async () => {
    try {
      setIsExporting(true);

      // Import xlsx dynamically
      const XLSX = await import('xlsx');

      // Get date range
      const { start, end } = getDateRange();

      // Prepare data for export
      const exportData = filteredTransactions.map(t => ({
        'Sana': new Date(t.created_at).toLocaleString('uz-UZ'),
        'Turi': getTransactionDisplayName(t),
        'Summa': Number(t.amount),
        "To'lov usuli": t.payment_method ? getPaymentMethodLabel(t.payment_method) : '-',
        'Izoh': t.description || '-'
      }));

      // Create workbook
      const wb = XLSX.utils.book_new();

      // Create worksheet
      const ws = XLSX.utils.json_to_sheet([]);

      let currentRow = 0;

      // Add title
      XLSX.utils.sheet_add_aoa(ws, [['Kunlik kassa hisoboti']], { origin: `A${currentRow + 1}` });
      currentRow += 2;

      // Add date info if period is more than 1 day or all time
      if (selectedPeriod !== 'today' && selectedPeriod !== 'yesterday') {
        XLSX.utils.sheet_add_aoa(ws, [['Yaratilgan sana:', new Date().toLocaleDateString('uz-UZ')]], { origin: `A${currentRow + 1}` });
        currentRow++;

        if (start && end) {
          XLSX.utils.sheet_add_aoa(ws, [['Dan:', start.toLocaleDateString('uz-UZ')]], { origin: `A${currentRow + 1}` });
          currentRow++;
          XLSX.utils.sheet_add_aoa(ws, [['Gacha:', end.toLocaleDateString('uz-UZ')]], { origin: `A${currentRow + 1}` });
          currentRow++;
        } else if (selectedPeriod === 'all') {
          XLSX.utils.sheet_add_aoa(ws, [['Davr:', 'Barcha vaqt']], { origin: `A${currentRow + 1}` });
          currentRow++;
        }
        currentRow++;
      } else {
        XLSX.utils.sheet_add_aoa(ws, [['Sana:', new Date().toLocaleDateString('uz-UZ')]], { origin: `A${currentRow + 1}` });
        currentRow += 2;
      }

      // Add summary
      XLSX.utils.sheet_add_aoa(ws, [['Xulosa:']], { origin: `A${currentRow + 1}` });
      currentRow++;
      XLSX.utils.sheet_add_aoa(ws, [['Kirim:', summary.total.income]], { origin: `A${currentRow + 1}` });
      currentRow++;
      XLSX.utils.sheet_add_aoa(ws, [['Chiqim:', summary.total.expenses]], { origin: `A${currentRow + 1}` });
      currentRow++;
      XLSX.utils.sheet_add_aoa(ws, [['Balans:', summary.total.balance]], { origin: `A${currentRow + 1}` });
      currentRow += 2;

      // Add transaction data
      XLSX.utils.sheet_add_json(ws, exportData, { origin: `A${currentRow + 1}` });

      // Add worksheet to workbook
      XLSX.utils.book_append_sheet(wb, ws, 'Kassa');

      // Generate filename
      const filename = `kunlik-kassa-${selectedPeriod}-${new Date().toISOString().split('T')[0]}.xlsx`;

      // Save file
      XLSX.writeFile(wb, filename);
    } catch (error) {
      console.error('Export error:', error);
      alert('Eksport qilishda xatolik yuz berdi');
    } finally {
      setIsExporting(false);
    }
  };

  const handlePrint = () => {
    try {
      const printContent = document.createElement('div');
      printContent.innerHTML = `
        <div style="padding: 20px;">
          <h1 style="font-size: 24px; margin-bottom: 20px;">Kunlik kassa hisoboti</h1>
          <p>Sana: ${new Date().toLocaleDateString()}</p>
          
          <div style="margin: 20px 0;">
            <h2 style="font-size: 18px;">Xulosa</h2>
            <p>Kirim: ${summary.total.income.toLocaleString()} UZS</p>
            <p>Chiqim: ${summary.total.expenses.toLocaleString()} UZS</p>
            <p>Balans: ${summary.total.balance.toLocaleString()} UZS</p>
          </div>
          
          <table style="width: 100%; border-collapse: collapse;">
            <thead>
              <tr>
                <th style="border: 1px solid #ddd; padding: 8px; text-align: left;">Sana</th>
                <th style="border: 1px solid #ddd; padding: 8px; text-align: left;">Turi</th>
                <th style="border: 1px solid #ddd; padding: 8px; text-align: left;">Summa</th>
                <th style="border: 1px solid #ddd; padding: 8px; text-align: left;">Izoh</th>
              </tr>
            </thead>
            <tbody>
              ${filteredTransactions.map(t => `
                <tr>
                  <td style="border: 1px solid #ddd; padding: 8px;">${new Date(t.created_at).toLocaleDateString()}</td>
                  <td style="border: 1px solid #ddd; padding: 8px;">${getTransactionDisplayName(t)}</td>
                  <td style="border: 1px solid #ddd; padding: 8px;">${t.amount.toLocaleString()} UZS</td>
                  <td style="border: 1px solid #ddd; padding: 8px;">${t.description || '-'}</td>
                </tr>
              `).join('')}
            </tbody>
          </table>
        </div>
      `;

      const printWindow = window.open('', '_blank');
      if (printWindow) {
        printWindow.document.write(`
          <!DOCTYPE html>
          <html>
          <head>
            <title>Kunlik kassa hisoboti</title>
            <style>
              body { font-family: Arial, sans-serif; }
              @media print {
                body { margin: 0; }
              }
            </style>
          </head>
          <body>
            ${printContent.innerHTML}
          </body>
          </html>
        `);
        printWindow.document.close();
        printWindow.focus();
        printWindow.print();
        printWindow.close();
      }
    } catch (error) {
      console.error('Print error:', error);
      alert('Chop etishda xatolik yuz berdi');
    }
  };

  const filteredTransactions = transactions.filter(t => 
    t.description?.toLowerCase().includes(searchQuery.toLowerCase()) || 
    getTransactionDisplayName(t).toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <div className="w-full px-0 pb-20 lg:pb-0">
      <div className="flex flex-col md:flex-row md:justify-end mb-4 md:mb-6">
        <button
          onClick={() => setShowAddModal(true)}
          className="flex items-center justify-center gap-2 px-4 py-2.5 md:py-2 min-h-[44px] lg:min-h-0 w-full md:w-auto bg-blue-600 text-white rounded-lg hover:bg-blue-700"
        >
          <Plus className="w-5 h-5" />
          {t('addTransaction')}
        </button>
      </div>

      {/* Period Selector */}
      <div className="mb-4 md:mb-6 flex flex-col md:flex-row md:items-center md:justify-between gap-3 md:gap-4">
        <div className="flex items-center gap-2 md:gap-3">
          <div className="flex items-center gap-2 text-gray-700">
            <Calendar className="w-5 h-5" />
            <span className="font-medium text-sm md:text-base">{t('period')}:</span>
          </div>
          <select
            value={selectedPeriod}
            onChange={(e) => setSelectedPeriod(e.target.value as Period)}
            className="flex-1 md:flex-none px-3 py-2 min-h-[44px] lg:min-h-0 text-sm md:text-base border border-gray-300 rounded-md bg-white text-gray-700 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          >
            <option value="today">{t('today')}</option>
            <option value="yesterday">{t('yesterday')}</option>
            <option value="week">{t('thisWeek')}</option>
            <option value="month">{t('thisMonth')}</option>
            <option value="all">Barchasi</option>
          </select>
        </div>
        <button
          onClick={handleExportXLSX}
          disabled={isExporting}
          className="flex items-center justify-center gap-2 px-4 py-2.5 md:py-2 min-h-[44px] lg:min-h-0 w-full md:w-auto border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {isExporting ? (
            <>
              <div className="w-5 h-5 border-2 border-gray-300 border-t-blue-600 rounded-full animate-spin" />
              Yuklanmoqda...
            </>
          ) : (
            <>
              <Download className="w-5 h-5" />
              Excel
            </>
          )}
        </button>
      </div>

      {error && (
        <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg flex items-start gap-3">
          <AlertCircle className="w-5 h-5 text-red-500 mt-0.5" />
          <p className="text-red-700">{error}</p>
        </div>
      )}

      {/* Summary Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-2 md:gap-4 lg:gap-4 mb-4 md:mb-6">
        <div className="bg-blue-50 p-3 md:p-4 lg:p-4 rounded-lg border border-blue-100">
          <div className="flex items-center gap-1.5 text-blue-600 mb-1 text-sm">
            <TrendingUp className="w-4 h-4" />
            {t('income')}
          </div>
          <div className="text-lg md:text-2xl font-bold text-blue-700 mb-2">
            {summary.total.income.toLocaleString()} UZS
          </div>
          <div className="space-y-0.5 pt-2 border-t border-blue-200">
            <div className="flex justify-between items-center text-sm">
              <span className="text-blue-600 flex items-center gap-1">
                <Banknote className="w-4 h-4" />
                Naqd:
              </span>
              <span className="font-medium text-blue-700">
                {summary.cash.income.toLocaleString()} UZS
              </span>
            </div>
            <div className="flex justify-between items-center text-sm">
              <span className="text-blue-600 flex items-center gap-1">
                <CreditCard className="w-4 h-4" />
                Karta:
              </span>
              <span className="font-medium text-blue-700">
                {summary.card.income.toLocaleString()} UZS
              </span>
            </div>
          </div>
        </div>
        <div className="bg-red-50 p-3 md:p-4 lg:p-4 rounded-lg border border-red-100">
          <div className="flex items-center gap-1.5 text-red-600 mb-1 text-sm">
            <TrendingDown className="w-4 h-4" />
            {t('expenses')}
          </div>
          <div className="text-lg md:text-2xl font-bold text-red-700 mb-2">
            {summary.total.expenses.toLocaleString()} UZS
          </div>
          <div className="space-y-0.5 pt-2 border-t border-red-200">
            <div className="flex justify-between items-center text-sm">
              <span className="text-red-600 flex items-center gap-1">
                <Banknote className="w-4 h-4" />
                Naqd:
              </span>
              <span className="font-medium text-red-700">
                {summary.cash.expenses.toLocaleString()} UZS
              </span>
            </div>
            <div className="flex justify-between items-center text-sm">
              <span className="text-red-600 flex items-center gap-1">
                <CreditCard className="w-4 h-4" />
                Karta:
              </span>
              <span className="font-medium text-red-700">
                {summary.card.expenses.toLocaleString()} UZS
              </span>
            </div>
          </div>
        </div>
        <div className="bg-green-50 p-3 md:p-4 lg:p-4 rounded-lg border border-green-100">
          <div className="flex items-center gap-1.5 text-green-600 mb-1 text-sm">
            <DollarSign className="w-4 h-4" />
            {t('balance')}
          </div>
          <div className="text-lg md:text-2xl font-bold text-green-700 mb-2">
            {summary.total.balance.toLocaleString()} UZS
          </div>
          <div className="space-y-0.5 pt-2 border-t border-green-200">
            <div className="flex justify-between items-center text-sm">
              <span className="text-green-600 flex items-center gap-1">
                <Banknote className="w-4 h-4" />
                Naqd:
              </span>
              <span className="font-medium text-green-700">
                {summary.cash.balance.toLocaleString()} UZS
              </span>
            </div>
            <div className="flex justify-between items-center text-sm">
              <span className="text-green-600 flex items-center gap-1">
                <CreditCard className="w-4 h-4" />
                Karta:
              </span>
              <span className="font-medium text-green-700">
                {summary.card.balance.toLocaleString()} UZS
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Transactions */}
      <div className="bg-white rounded-lg shadow-sm">
        <div className="p-3 md:p-4 lg:p-4 border-b">
          <div className="flex items-center gap-2">
            <Search className="w-5 h-5 text-gray-400" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder={t('searchTransactions')}
              className="w-full px-3 py-1.5 md:px-4 md:py-2 text-sm md:text-base border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>
        </div>

        {/* Mobile: Card list */}
        <div className="lg:hidden p-3 md:p-4 space-y-1.5 md:space-y-3">
          {loading ? (
            <div className="py-8 text-center text-gray-500">{t('loading')}</div>
          ) : filteredTransactions.length > 0 ? (
            filteredTransactions.map((transaction) => (
              <div key={transaction.id} className="bg-gray-50 p-3 md:p-4 rounded-lg border border-gray-200">
                <div className="flex justify-between items-start gap-2">
                  <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium shrink-0 ${
                    transaction.type === 'income' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                  }`}>
                    {getTransactionDisplayName(transaction)}
                  </span>
                  <span className={`text-sm md:text-base font-semibold shrink-0 ${transaction.type === 'income' ? 'text-green-600' : 'text-red-600'}`}>
                    {transaction.type === 'income' ? '+' : '-'}{transaction.amount.toLocaleString()} UZS
                  </span>
                </div>
                <div className="text-xs md:text-sm text-gray-500 mt-1.5">
                  {new Date(transaction.created_at).toLocaleString()} • {transaction.payment_method ? getPaymentMethodLabel(transaction.payment_method) : '-'}
                </div>
                {transaction.description && (
                  <p className="text-sm text-gray-600 mt-1">{transaction.description}</p>
                )}
              </div>
            ))
          ) : (
            <div className="py-8 text-center text-gray-500">{t('noTransactionsFound')}</div>
          )}
        </div>

        {/* Desktop: Table */}
        <div className="hidden lg:block overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="bg-gray-50">
                <th className="px-6 py-3 text-left text-sm font-medium text-gray-500">{t('date')}</th>
                <th className="px-6 py-3 text-left text-sm font-medium text-gray-500">{t('type')}</th>
                <th className="px-6 py-3 text-left text-sm font-medium text-gray-500">{t('amount')}</th>
                <th className="px-6 py-3 text-left text-sm font-medium text-gray-500">To&apos;lov usuli</th>
                <th className="px-6 py-3 text-left text-sm font-medium text-gray-500">{t('description')}</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {loading ? (
                <tr>
                  <td colSpan={5} className="px-6 py-4 text-center text-gray-500">
                    {t('loading')}
                  </td>
                </tr>
              ) : filteredTransactions.length > 0 ? (
                filteredTransactions.map((transaction) => (
                  <tr key={transaction.id}>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {new Date(transaction.created_at).toLocaleString()}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                        transaction.type === 'income'
                          ? 'bg-green-100 text-green-800'
                          : 'bg-red-100 text-red-800'
                      }`}>
                        {getTransactionDisplayName(transaction)}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm">
                      <span className={transaction.type === 'income' ? 'text-green-600' : 'text-red-600'}>
                        {transaction.amount.toLocaleString()} UZS
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {transaction.payment_method ? getPaymentMethodLabel(transaction.payment_method) : '-'}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-500">
                      {transaction.description || '-'}
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={5} className="px-6 py-4 text-center text-gray-500">
                    {t('noTransactionsFound')}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Add Transaction Modal */}
      {showAddModal && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60] p-4"
          onClick={() => setShowAddModal(false)}
        >
          <div 
            className="bg-white rounded-lg shadow-xl max-w-md w-full max-h-[90vh] flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex justify-between items-center p-6 border-b flex-shrink-0">
              <h3 className="text-xl font-semibold text-gray-900">{t('addTransaction')}</h3>
              <button
                onClick={() => setShowAddModal(false)}
                className="text-gray-400 hover:text-gray-500"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="overflow-y-auto flex-1 px-6 py-4">
            {formError && (
              <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-md text-red-700 text-sm">
                {formError}
              </div>
            )}

              <form onSubmit={handleSubmit} className="space-y-4" id="transaction-form">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t('type')}
                </label>
                <div className="grid grid-cols-2 w-full" role="group">
                  <label className="relative inline-flex items-center px-4 py-3 md:px-8 md:py-4 text-base font-medium border border-gray-300 rounded-l-md cursor-pointer hover:bg-gray-50 focus-within:z-10 focus-within:ring-2 focus-within:ring-green-500 focus-within:border-green-500">
                    <input
                      type="radio"
                      name="transactionType"
                      value="income"
                      checked={formData.type === 'income'}
                      onChange={(e) => setFormData(prev => ({ ...prev, type: e.target.value as 'income' | 'expense' }))}
                      className="sr-only"
                    />
                    <span className={`flex-1 text-center relative z-10 ${formData.type === 'income' ? 'text-green-600 font-medium' : 'text-gray-700'}`}>
                      Kirim
                    </span>
                    {formData.type === 'income' && (
                      <div className="absolute inset-0 bg-green-100 border-2 border-green-500 rounded-l-md pointer-events-none z-0"></div>
                    )}
                  </label>
                  <label className="relative inline-flex items-center px-4 py-3 md:px-8 md:py-4 text-base font-medium border border-gray-300 rounded-r-md cursor-pointer hover:bg-gray-50 focus-within:z-10 focus-within:ring-2 focus-within:ring-red-500 focus-within:border-red-500 -ml-px">
                    <input
                      type="radio"
                      name="transactionType"
                      value="expense"
                      checked={formData.type === 'expense'}
                      onChange={(e) => setFormData(prev => ({ ...prev, type: e.target.value as 'income' | 'expense' }))}
                      className="sr-only"
                    />
                    <span className={`flex-1 text-center relative z-10 ${formData.type === 'expense' ? 'text-red-600 font-medium' : 'text-gray-700'}`}>
                      Chiqim
                    </span>
                    {formData.type === 'expense' && (
                      <div className="absolute inset-0 bg-red-100 border-2 border-red-500 rounded-r-md pointer-events-none z-0"></div>
                    )}
                  </label>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t('amount')}
                </label>
                <input
                  type="number"
                  min="0"
                  step="1000"
                  value={formData.amount}
                  onChange={(e) => setFormData(prev => ({ ...prev, amount: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="0"
                />
              </div>

              {formData.type === 'income' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Daromad turi
                  </label>
                  <div className="grid grid-cols-2 gap-2 md:gap-3">
                    {incomeTypes.filter((t) => t.is_active).map((incomeType) => (
                      <div key={incomeType.id} className="relative">
                        <button
                          type="button"
                          onClick={() => setFormData(prev => ({ ...prev, incomeTypeId: incomeType.id }))}
                          className={`w-full h-full p-2 md:p-3 border rounded-lg text-sm transition-colors
                            ${
                              formData.incomeTypeId === incomeType.id
                                ? 'border-blue-500 bg-blue-50 text-blue-700'
                                : 'border-gray-200 hover:border-gray-300'
                            }`}
                        >
                          {incomeType.name.charAt(0).toUpperCase() + incomeType.name.slice(1)}
                        </button>
                        <button
                          type="button"
                          onClick={(e) => openEditCashType(e, { kind: 'income', type: incomeType })}
                          className="absolute -top-1.5 -right-1.5 w-5 h-5 bg-white border border-gray-300 rounded-full flex items-center justify-center shadow-sm hover:bg-blue-50 hover:border-blue-400 transition-colors"
                          title="Tahrirlash"
                          aria-label={`${incomeType.name} turini tahrirlash`}
                        >
                          <Pencil className="w-2.5 h-2.5 text-gray-500" />
                        </button>
                      </div>
                    ))}
                    <button
                      type="button"
                      onClick={() => setFormData(prev => ({ ...prev, incomeTypeId: 'boshqa' }))}
                      className={`p-2 md:p-3 border rounded-lg text-sm transition-colors
                        ${
                          formData.incomeTypeId === 'boshqa'
                            ? 'border-blue-500 bg-blue-50 text-blue-700'
                            : 'border-gray-200 hover:border-gray-300'
                        }`}
                    >
                      Boshqa
                    </button>
                  </div>
                </div>
              )}

              {formData.type === 'expense' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Xarajat turi
                  </label>
                  <div className="grid grid-cols-2 gap-2 md:gap-3">
                    {expenseTypes.filter((t) => t.is_active).map((expenseType) => (
                      <div key={expenseType.id} className="relative">
                        <button
                          type="button"
                          onClick={() => setFormData(prev => ({ ...prev, expenseTypeId: expenseType.id }))}
                          className={`w-full h-full p-2 md:p-3 border rounded-lg text-sm transition-colors
                            ${
                              formData.expenseTypeId === expenseType.id
                                ? 'border-blue-500 bg-blue-50 text-blue-700'
                                : 'border-gray-200 hover:border-gray-300'
                            }`}
                        >
                          {expenseType.name.charAt(0).toUpperCase() + expenseType.name.slice(1)}
                        </button>
                        <button
                          type="button"
                          onClick={(e) => openEditCashType(e, { kind: 'expense', type: expenseType })}
                          className="absolute -top-1.5 -right-1.5 w-5 h-5 bg-white border border-gray-300 rounded-full flex items-center justify-center shadow-sm hover:bg-blue-50 hover:border-blue-400 transition-colors"
                          title="Tahrirlash"
                          aria-label={`${expenseType.name} turini tahrirlash`}
                        >
                          <Pencil className="w-2.5 h-2.5 text-gray-500" />
                        </button>
                      </div>
                    ))}
                    <button
                      type="button"
                      onClick={() => setFormData(prev => ({ ...prev, expenseTypeId: 'boshqa' }))}
                      className={`p-2 md:p-3 border rounded-lg text-sm transition-colors
                        ${
                          formData.expenseTypeId === 'boshqa'
                            ? 'border-blue-500 bg-blue-50 text-blue-700'
                            : 'border-gray-200 hover:border-gray-300'
                        }`}
                    >
                      Boshqa
                    </button>
                  </div>
                </div>
              )}

              {formData.type === 'income' && formData.incomeTypeId === 'boshqa' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Yangi daromad turi
                  </label>
                  <input
                    type="text"
                    value={formData.customIncomeTypeName}
                    onChange={(e) => setFormData(prev => ({ ...prev, customIncomeTypeName: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="Daromad turini kiriting"
                  />
                </div>
              )}

              {formData.type === 'expense' && formData.expenseTypeId === 'boshqa' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Yangi xarajat turi
                  </label>
                  <input
                    type="text"
                    value={formData.customExpenseTypeName}
                    onChange={(e) => setFormData(prev => ({ ...prev, customExpenseTypeName: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="Xarajat turini kiriting"
                  />
                </div>
              )}

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  To&apos;lov usuli
                </label>
                <div className="grid grid-cols-2 gap-3">
                  <label className="relative inline-flex items-center justify-center gap-2 px-4 py-3 min-h-[44px] md:min-h-0 border border-gray-300 rounded-lg cursor-pointer hover:bg-gray-50 focus-within:z-10 focus-within:ring-2 focus-within:ring-blue-500 focus-within:border-blue-500">
                    <input
                      type="radio"
                      name="paymentMethod"
                      value="card"
                      checked={formData.paymentMethod === 'card'}
                      onChange={(e) => setFormData(prev => ({ ...prev, paymentMethod: e.target.value as PaymentMethod }))}
                      className="sr-only"
                    />
                    <CreditCard className={`w-5 h-5 relative z-10 ${formData.paymentMethod === 'card' ? 'text-blue-600' : 'text-gray-500'}`} />
                    <span className={`flex-1 text-center relative z-10 ${formData.paymentMethod === 'card' ? 'text-blue-600 font-medium' : 'text-gray-700'}`}>
                      Karta orqali
                    </span>
                    {formData.paymentMethod === 'card' && (
                      <div className="absolute inset-0 bg-blue-50 border-2 border-blue-500 rounded-lg pointer-events-none z-0"></div>
                    )}
                  </label>
                  <label className="relative inline-flex items-center justify-center gap-2 px-4 py-3 min-h-[44px] md:min-h-0 border border-gray-300 rounded-lg cursor-pointer hover:bg-gray-50 focus-within:z-10 focus-within:ring-2 focus-within:ring-blue-500 focus-within:border-blue-500">
                    <input
                      type="radio"
                      name="paymentMethod"
                      value="cash"
                      checked={formData.paymentMethod === 'cash'}
                      onChange={(e) => setFormData(prev => ({ ...prev, paymentMethod: e.target.value as PaymentMethod }))}
                      className="sr-only"
                    />
                    <Banknote className={`w-5 h-5 relative z-10 ${formData.paymentMethod === 'cash' ? 'text-blue-600' : 'text-gray-500'}`} />
                    <span className={`flex-1 text-center relative z-10 ${formData.paymentMethod === 'cash' ? 'text-blue-600 font-medium' : 'text-gray-700'}`}>
                      Naqd
                    </span>
                    {formData.paymentMethod === 'cash' && (
                      <div className="absolute inset-0 bg-blue-50 border-2 border-blue-500 rounded-lg pointer-events-none z-0"></div>
                    )}
                  </label>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t('description')}
                </label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 h-24"
                  placeholder={t('enterDescription')}
                />
              </div>
              </form>
              </div>

            <div className="flex justify-end gap-4 p-6 border-t flex-shrink-0">
                <button
                  type="button"
                  onClick={() => setShowAddModal(false)}
                  className="px-4 py-2 border border-gray-300 rounded-md hover:bg-gray-50"
                >
                  {t('cancel')}
                </button>
                <button
                  type="submit"
                form="transaction-form"
                  disabled={loading}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
                >
                  {loading ? t('saving') : t('save')}
                </button>
              </div>
          </div>
        </div>
      )}

      {/* Edit / delete a cash category (opened from the pencil on a type button) */}
      {editingCashType && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[70] p-4"
          onClick={closeEditCashType}
        >
          <div
            className="bg-white rounded-lg shadow-xl max-w-md w-full"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex justify-between items-center px-6 py-4 border-b">
              <h3 className="text-lg font-semibold text-gray-900">
                {editingCashType.kind === 'income' ? 'Daromad turini tahrirlash' : 'Xarajat turini tahrirlash'}
              </h3>
              <button onClick={closeEditCashType} className="text-gray-400 hover:text-gray-500" aria-label="Yopish">
                <X className="w-5 h-5" />
              </button>
            </div>

            <form
              className="px-6 py-4"
              onSubmit={(e) => {
                e.preventDefault();
                handleSaveCashType();
              }}
            >
              <label className="block text-sm font-medium text-gray-700 mb-2">Nomi</label>
              <input
                type="text"
                value={editCashTypeName}
                onChange={(e) => {
                  setEditCashTypeName(e.target.value);
                  setConfirmDeleteCashType(false); // editing the name disarms a pending delete
                }}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                autoFocus
              />
              {confirmDeleteCashType && (
                <p className="mt-3 text-sm text-red-700 bg-red-50 border border-red-200 rounded-md p-2">
                  Bu tur ro'yxatdan olib tashlanadi. Avvalgi tranzaksiyalarda nomi saqlanib qoladi.
                  O'chirish uchun yana bir marta bosing.
                </p>
              )}
              {cashTypeError && (
                <p className="mt-3 text-sm text-red-600 flex items-center gap-1.5">
                  <AlertCircle className="w-4 h-4" />
                  {cashTypeError}
                </p>
              )}
            </form>

            <div className="flex justify-between gap-3 px-6 py-4 border-t">
              <button
                type="button"
                onClick={handleDeleteCashType}
                disabled={savingCashType}
                className={`px-4 py-2 rounded-md flex items-center gap-2 disabled:opacity-50 ${
                  confirmDeleteCashType
                    ? 'bg-red-600 text-white hover:bg-red-700'
                    : 'border border-red-300 text-red-600 hover:bg-red-50'
                }`}
              >
                <Trash2 className="w-4 h-4" />
                {confirmDeleteCashType ? "Ha, o'chirish" : "O'chirish"}
              </button>
              <div className="flex gap-3">
                <button
                  type="button"
                  onClick={closeEditCashType}
                  className="px-4 py-2 border border-gray-300 rounded-md hover:bg-gray-50"
                >
                  Bekor qilish
                </button>
                <button
                  type="button"
                  onClick={handleSaveCashType}
                  disabled={!editCashTypeName.trim() || savingCashType}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                >
                  {savingCashType && <Loader2 className="w-4 h-4 animate-spin" />}
                  {savingCashType ? 'Saqlanmoqda...' : 'Saqlash'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DailyCash;